import {
	IFileUploadResponse,
	IRequestObject,
	Operation,
} from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { DocumentNode } from 'graphql';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { Injectable } from '@angular/core';
import { createFileMutation } from '@shared/ui-components/au-file-loader/au-file-loader.queries';
import { LocationInput } from '@app/pages/cmms/interfaces/cmms-global.interfaces';

@Injectable({ providedIn: 'root' })
export class AuFileLoaderCore {
	constructor(
		private http: HttpClient,
		private keycloakService: KeycloakService
	) {}

	makeRequest(
		requestConfig: IRequestObject
	): Observable<{ event: HttpResponse<IFileUploadResponse>; index: number }> {
		const mutation = requestConfig.mutation || createFileMutation;

		const fileOperations = this.getFileOperationObject(
			mutation,
			requestConfig.file,
			requestConfig.mutationVariables
		);

		return this.startGraphQlUponHttpRequest(
			requestConfig.httpUri,
			requestConfig.file,
			fileOperations,
			requestConfig.index,
			requestConfig.requestBrakeSubject$
		);
	}

	private startGraphQlUponHttpRequest(
		url: string,
		file: File,
		operations: Operation,
		index: number,
		requestBrakeSubject$: Subject<void>
	): Observable<{ event: HttpResponse<any>; index: number }> {
		const formData = this.getFormData(operations, file);

		return from(this.keycloakService.getToken()).pipe(
			switchMap((token: string) => this.getHttpRequest(url, formData, token)),
			takeUntil(requestBrakeSubject$),
			switchMap(event => of({ event, index })),
			catchError(err => {
				return of({
					index,
					event: err,
				});
			})
		);
	}

	private getHttpRequest(
		url: string,
		formData: FormData,
		token: string
	): Observable<any> {
		return this.http.post(url, formData, {
			headers: {
				authorization: 'Bearer ' + token,
			},
			reportProgress: true,
			observe: 'events',
		});
	}

	private getFormData(operations: Operation, file): FormData {
		const formData = new FormData();

		const map = {
			file: ['variables.file'],
		};

		formData.append('operations', JSON.stringify(operations));
		formData.append('map', JSON.stringify(map));
		formData.append('file', file);

		return formData;
	}

	private getFileOperationObject(
		query: DocumentNode,
		file: File,
		mutationVariables: { [key: string]: string | LocationInput[] } = {}
	): Operation {
		return {
			query: query.loc.source.body,
			variables: {
				file: null,
				name: this.trimName(file.name),
				...mutationVariables,
			},
		};
	}

	private trimName(name: string): string {
		if (name.length > 140) {
			const extension = name.split('.').pop();
			return name.substring(0, 140) + '.' + extension;
		}

		return name;
	}
}
